// Sheet's swipe-down-to-dismiss, as two pure decisions.
//
// The gesture is the most intricate thing in that component and, until wave 2,
// the least tested — which was not a coincidence. Reaching a guard from a test
// meant synthesizing a whole pointer sequence tuned to trip exactly that guard,
// so the guard matrix, which is where the bugs would be, cost a multi-step
// setup per row. Pulled out here it is a table of numbers in and verdicts out,
// and the handlers left behind are plumbing: capture the pointer, ask the
// function, apply the offset.
//
// Beside the component rather than inside it because `<script setup>` compiles
// to a default export and cannot carry named ones — the same reason
// bidCardShapes.js sits here. The pointer-driven flow tests are still written
// against the component; this stops being the ONLY place any of it is checked.

/** Past this much downward travel, letting go dismisses rather than springs back. */
export const DISMISS_PX = 110;

/**
 * Below this a gesture is not yet a gesture — it is a press that moved.
 *
 * Judging direction off the first pixel would classify every tap, and the sign
 * of one pixel of jitter is noise rather than intent.
 */
export const MOVEMENT_FLOOR_PX = 4;

/**
 * Whether a gesture in progress is a dismiss, a scroll, or not yet either —
 * `'drag' | 'ignore' | 'wait'`.
 *
 * Three guards, each of which exists because something real broke:
 *
 * - an upward or barely-moving drag is left to the body's own scroll;
 * - a mostly-horizontal gesture is not taken over, so a horizontal control
 *   inside the sheet — the bid tray's slide-to-confirm — still works;
 * - a gesture that began in already-scrolled content is a scroll, not a
 *   dismiss.
 *
 * `wait` is the one verdict that is not a decision: the gesture has not moved
 * far enough to classify, and the next move event asks again. It is separate
 * from `ignore` because they are opposite instructions to the caller — one
 * keeps tracking, the other stops.
 *
 * @param {object} gesture
 * @param {number} gesture.deltaX  Horizontal travel since the pointer went down.
 * @param {number} gesture.deltaY  Vertical travel; positive is downward.
 * @param {number} gesture.scrollTop  Scroll offset of the body the gesture began in.
 */
export function gestureVerdict({ deltaX, deltaY, scrollTop }) {
  if (deltaY < -MOVEMENT_FLOOR_PX) return 'ignore';
  if (deltaY < MOVEMENT_FLOOR_PX) return 'wait';
  if (Math.abs(deltaX) > deltaY) return 'ignore';
  if (scrollTop > 0) return 'ignore';
  return 'drag';
}

/** Whether letting go at this offset dismisses the sheet, or springs it back to rest. */
export function dismissesOnRelease(offset) {
  return offset > DISMISS_PX;
}
